/**
 * Transaction Dialog Context
 *
 * Provides global state management for transaction progress dialogs.
 * Replaces corner toasts with centered dialogs for better UX.
 */

import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

export type TransactionType =
  | 'create-coin'   // "Creating coin: NAME [TICKER]"
  | 'quick-buy'     // "Buying X SOL of TOKEN"
  | 'quick-sell'    // "Selling X TOKEN for Y SOL"
  | 'claim-rewards' // "Claiming X SOL"
  | 'generic';

export type TransactionDialogState = 'idle' | 'executing' | 'confirmed' | 'failed';

export interface TransactionMetadata {
  type: TransactionType;
  tokenName?: string;
  tokenSymbol?: string;
  amount?: number;
  amountOut?: number;
  signature?: string;
  mintAddress?: string;
  errorMessage?: string;
}

export interface TransactionDialogContextValue {
  isOpen: boolean;
  state: TransactionDialogState;
  metadata: TransactionMetadata | null;
  countdownSeconds: number | null;

  startTransaction: (metadata: TransactionMetadata) => void;
  setConfirmed: (signature: string, additionalMeta?: Partial<TransactionMetadata>) => void;
  setFailed: (errorMessage: string) => void;
  dismiss: () => void;
  retry: () => void;
}

const TransactionDialogContext = createContext<TransactionDialogContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

const AUTO_DISMISS_SECONDS = 10;

export function TransactionDialogProvider({ children }: ProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<TransactionDialogState>('idle');
  const [metadata, setMetadata] = useState<TransactionMetadata | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);

  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const retryCallbackRef = useRef<(() => void) | null>(null);

  // Clear countdown timer
  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdownSeconds(null);
  }, []);

  // Start countdown for auto-dismiss
  const startCountdown = useCallback(() => {
    clearCountdown();
    setCountdownSeconds(AUTO_DISMISS_SECONDS);

    countdownRef.current = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev === null || prev <= 1) {
          clearCountdown();
          setIsOpen(false);
          setState('idle');
          setMetadata(null);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearCountdown]);

  const startTransaction = useCallback((meta: TransactionMetadata) => {
    clearCountdown();
    setMetadata(meta);
    setState('executing');
    setIsOpen(true);
    retryCallbackRef.current = null;
  }, [clearCountdown]);

  const setConfirmed = useCallback((signature: string, additionalMeta?: Partial<TransactionMetadata>) => {
    setMetadata(prev => ({
      ...prev,
      ...additionalMeta,
      signature,
      type: prev?.type || 'generic',
    }));
    setState('confirmed');
    startCountdown();
  }, [startCountdown]);

  const setFailed = useCallback((errorMessage: string) => {
    setMetadata(prev => ({
      ...prev,
      errorMessage,
      type: prev?.type || 'generic',
    }));
    setState('failed');
  }, []);

  const dismiss = useCallback(() => {
    clearCountdown();
    setIsOpen(false);
    // Reset state after animation
    setTimeout(() => {
      setState('idle');
      setMetadata(null);
    }, 200);
  }, [clearCountdown]);

  const retry = useCallback(() => {
    // Retry callback should be set by the calling code
    if (retryCallbackRef.current) {
      retryCallbackRef.current();
    }
  }, []);

  const value: TransactionDialogContextValue = {
    isOpen,
    state,
    metadata,
    countdownSeconds,
    startTransaction,
    setConfirmed,
    setFailed,
    dismiss,
    retry,
  };

  return (
    <TransactionDialogContext.Provider value={value}>
      {children}
    </TransactionDialogContext.Provider>
  );
}

export function useTransactionDialogContext(): TransactionDialogContextValue {
  const context = useContext(TransactionDialogContext);
  if (!context) {
    throw new Error('useTransactionDialogContext must be used within TransactionDialogProvider');
  }
  return context;
}

export { TransactionDialogContext };
